import React, { useEffect, useId, useRef, useState } from "react";
import { X } from "lucide-react";
import QtyStepper from "./QtyStepper";
import { formatMoney, toCents } from "../../context/CartContext";

const MAX_QTY = 99;

// Pick the option(s) and quantity for an item with several priced options
// (Roll / Wedge / Hero, or a bare "$12.95 / $56" price pair). Every option has
// its own quantity, so a customer can take 2 of one and 1 of another in one go;
// each chosen option becomes its own line in the cart. The parent mounts this
// only while it is open, so quantities always start at zero for the item.
//
// onAdd([{ variation: { label, price }, qty }, ...]) fires only when at least
// one option has a quantity above zero.
export default function VariationModal({ item, onClose, onAdd }) {
  const titleId = useId();
  const dialogRef = useRef(null);
  const [quantities, setQuantities] = useState({});

  // Only well-formed options can be chosen; order is the merchant's order.
  const options = (Array.isArray(item?.variations) ? item.variations : []).filter(
    (v) => v && String(v.label ?? "").trim() && toCents(v.price) > 0,
  );
  const chosen = options.filter((v) => (quantities[v.label] || 0) > 0);
  const totalQty = chosen.reduce((sum, v) => sum + quantities[v.label], 0);
  const totalCents = chosen.reduce((sum, v) => sum + toCents(v.price) * quantities[v.label], 0);

  const setQty = (label, next) =>
    setQuantities((q) => ({ ...q, [label]: Math.max(0, Math.min(MAX_QTY, next)) }));

  useEffect(() => {
    dialogRef.current?.focus();
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const handleAdd = () => {
    if (chosen.length === 0) return;
    onAdd(chosen.map((v) => ({ variation: { label: v.label, price: v.price }, qty: quantities[v.label] })));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center sm:p-6"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="max-h-[92dvh] w-full overflow-y-auto rounded-t-2xl bg-white p-4 shadow-2xl outline-none sm:max-w-sm sm:rounded-2xl sm:p-5"
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <h2 id={titleId} className="text-base font-semibold text-gray-900">
            {item.description}
          </h2>
          <button type="button" onClick={onClose} className="rounded p-1 text-gray-500 hover:bg-gray-100" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {options.length === 0 ? (
          <p className="py-4 text-sm text-gray-500">These options are unavailable right now.</p>
        ) : (
          <>
            <p className="mb-2 text-sm font-medium text-gray-600">Set a quantity for each option you want</p>
            <ul className="space-y-2">
              {options.map((v) => {
                const qty = quantities[v.label] || 0;
                const display = v.unnamed ? formatMoney(toCents(v.price)) : v.label;
                return (
                  <li
                    key={v.label}
                    className={`flex items-center justify-between gap-3 rounded-xl border px-3 py-2.5 text-sm transition ${
                      qty > 0 ? "border-blue-600 bg-blue-50" : "border-gray-200"
                    }`}
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900">{display}</p>
                      {!v.unnamed && <p className="tabular-nums text-gray-500">{formatMoney(toCents(v.price))}</p>}
                    </div>
                    <QtyStepper
                      size="sm"
                      qty={qty}
                      label={`${item.description} ${display}`}
                      onDecrement={() => setQty(v.label, qty - 1)}
                      onIncrement={() => setQty(v.label, qty + 1)}
                    />
                  </li>
                );
              })}
            </ul>
          </>
        )}

        <p className="mt-4 text-sm text-gray-600" aria-live="polite">
          {totalQty > 0 ? (
            <>
              {totalQty} {totalQty === 1 ? "item" : "items"} ={" "}
              <span className="font-semibold tabular-nums text-gray-900">{formatMoney(totalCents)}</span>
            </>
          ) : (
            "Select an option"
          )}
        </p>

        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAdd}
            disabled={totalQty === 0}
            className="flex-1 rounded-xl bg-blue-600 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
          >
            Add to Cart
          </button>
        </div>
      </div>
    </div>
  );
}
